import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import type { ChatPresenterComponent } from '@core/tellent-assistant-contracts';
import type { PresentTableArgs } from '@core/tellent-assistant-types';
import {
  DataTableComponent,
  HeadingComponent,
  IconButtonComponent,
  type NewDataTableColumn,
} from '@core/user-interface-legacy';

import {
  buildColumn,
  compareValues,
  inferFormat,
  rowId,
  type AssistantTableRow,
} from './present-table-data.helpers';

const PAGE_SIZE = 10;

interface SortState {
  readonly key: string;
  readonly dir: 'asc' | 'desc';
}

@Component({
  selector: 'rt-assistant-present-table',
  imports: [DataTableComponent, HeadingComponent, IconButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './present-table-data.component.html',
  styleUrl: './present-table-data.component.less',
})
export class PresentTableDataComponent implements ChatPresenterComponent<PresentTableArgs> {
  private readonly colsBase = computed(() => {
    const rows = this.args().rows;
    return this.args().columns.map(c => {
      const inferred = c.format ?? inferFormat(c.key, rows);
      return buildColumn(c, inferred);
    });
  });

  private readonly sortedSrc = computed(() => {
    const rows = this.args().rows;
    const sort = this.sortState();
    if (!sort) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compareValues(a[sort.key], b[sort.key]) * dir);
  });

  protected readonly page = signal(0);
  protected readonly sortState = signal<SortState | null>(null);
  protected readonly pageSize = PAGE_SIZE;

  protected readonly title = computed(() => this.args().title);
  protected readonly rowsCount = computed(() => this.args().rows.length);
  protected readonly hasRows = computed(() => this.rowsCount() > 0);

  protected readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.rowsCount() / this.pageSize)),
  );
  protected readonly canPrev = computed(() => this.page() > 0);
  protected readonly canNext = computed(() => this.page() < this.totalPages() - 1);

  protected readonly cols = computed(() => {
    const sort = this.sortState();
    return this.colsBase().map(c => ({
      ...c,
      sort: c.id === sort?.key ? sort.dir : null,
    }));
  });

  protected readonly rows = computed<AssistantTableRow[]>(() => {
    const start = this.page() * this.pageSize;
    return this.sortedSrc()
      .slice(start, start + this.pageSize)
      .map((src, i) => ({ id: rowId(src, start + i), _src: src }));
  });

  public readonly args = input.required<PresentTableArgs>();

  protected onSortChange(col: NewDataTableColumn<AssistantTableRow>): void {
    this.page.set(0);
    this.sortState.update(prev => {
      if (!prev || prev.key !== col.id) return { key: col.id, dir: 'asc' };
      if (prev.dir === 'asc') return { key: col.id, dir: 'desc' };
      return null;
    });
  }

  protected nextPage(): void {
    this.page.update(p => Math.min(p + 1, this.totalPages() - 1));
  }

  protected prevPage(): void {
    this.page.update(p => Math.max(p - 1, 0));
  }
}
